<template>
  <div class="space-y-4">
    <h3>
      {{ t('customerData') }}
    </h3>

    <div class="mb-2 grid gap-2 md:grid-cols-2">
      <SearchApiEntityItemInput
        :label="t('searchCustomer')"
        :placeholder="t('searchByNameEmailOrNumber')"
        :search-result-options="searchResultOptions"
        :is-fetching-results="isFetching"
        @fetch="fetchSuggestions"
        @update-selected-item="handleSelectSuggestion"
      />
    </div>

    <CustomerDataForm hide-header />

    <div v-if="$can('create', 'Booking', 'taxProcedure')" class="grid gap-4 md:grid-cols-3">
      <TaxProcedureSelect
        class="col-start-1"
        :model-value="taxProcedure"
        :vat-id="formValues.vatId"
        @update:model-value="(value) => $emit('update:taxProcedure', value)"
      />
    </div>

    <Divider />

    <div v-if="agency">
      <Agency :agency="agency" />
      <Divider />
    </div>

    <h3>
      {{ t('bookingSummary') }}
    </h3>

    <div class="grid items-start gap-4 md:grid-cols-2">
      <VehicleInfo :car="car" :start-date="startDate" />

      <LocationsAndTimesInfo :create-booking-props="props.createBookingProps" />
    </div>

    <Divider />

    <div v-if="hasTransfers">
      <Transfers :car="car" :create-booking-props="props.createBookingProps" />

      <Divider />
    </div>

    <div>
      <Extras :price-calculation="pricing!" immutable />
      <Divider />
    </div>

    <div class="grid flex-row-reverse gap-2 md:flex md:items-end md:justify-between">
      <div class="grid items-end gap-4 md:flex">
        <TotalPrice :total-price="pricing!.totalPrice" />
        <CVButton variant="default" :is-loading="isSaving" @click="submitForm">{{
          t('createBooking')
        }}</CVButton>
      </div>
      <CVButton variant="link" :disabled="isSaving" @click="back">{{ t('back') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Divider from '@/components/Divider.vue';
import CustomerDataForm from '@/components/booking-forms/customer/CustomerDataForm.vue';
import { Gender } from '@/entities/auth/gender.enum';
import type { CustomerDataDto } from '@/entities/bookings/customer-data.dto';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { object, string } from 'yup';
import TotalPrice from '../../TotalPrice.vue';
import { cloneDeep } from 'lodash';
import { Local } from '@/entities/auth/local.enum';
import SearchApiEntityItemInput from '@/components/SearchApiEntityItemInput.vue';
import { useSuggestCustomerData } from '@/queries/use-bookings';
import type { User } from '@/entities/auth/user.entity';
import Agency from './Agency.vue';
import LocationsAndTimesInfo from '../../LocationsAndTimesInfo.vue';
import { computed, nextTick, ref, watch } from 'vue';
import type { Car } from '@/entities/cars/car.entity';
import Transfers from '../overview-and-extras/Transfers.vue';
import Extras from '@/components/extras/ExtrasForm.vue';
import VehicleInfo from './VehicleInfo.vue';
import type { CreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import type { BookingsControllerSuggestCustomerDataResponse } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';
import { UserRole } from '@/entities/auth/user-role.enum';
import { useVatIdValidation } from '@/hooks/use-vat-id-validation';
import { normalizeVatId } from '@/utils/vat-id';
import { vatIdSchema } from '@/validation/vat-id.schema';
import TaxProcedureSelect from '@/components/TaxProcedureSelect.vue';
import { TaxProcedure } from '@carvia/ros-client-types';

type CustomerDataWithEmail = CustomerDataDto & { email?: string | null };

const props = defineProps<{
  customerData: CustomerDataWithEmail;
  taxProcedure: TaxProcedure;
  agency: User | null;
  createBookingProps: CreateBookingProps;
  car: Car;
  isLoading: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:customerData', value: CustomerDataWithEmail): void;
  (e: 'update:taxProcedure', value: TaxProcedure): void;
  (e: 'update:tax-inputs', value: { vatId?: string | null; country?: string | null }): void;
  (e: 'apply-tax-procedure-suggestion'): void;
  (e: 'continue'): void;
  (e: 'back'): void;
  (e: 'suggestion-selected', value: { customerId?: string | null; email?: string | null }): void;
  (e: 'update-agency', value?: User | null): void;
}>();

const { startDate, pricing, transferDeliveryPrice, transferReturnPrice } = props.createBookingProps;

const { t } = useI18n();
const { isValidatingVatId, validateVatId } = useVatIdValidation();

const hasTransfers = computed(() => transferDeliveryPrice.value || transferReturnPrice.value);
const isSaving = computed(() => props.isLoading || isValidatingVatId.value);

const {
  handleSubmit,
  values: formValues,
  setValues,
} = useForm({
  initialValues: { ...props.customerData },
  validationSchema: object({
    vatId: vatIdSchema()
      .test(
        'reverseChargeRequiresVatId',
        'VAT ID is required for reverse charge',
        function (value) {
          if (props.taxProcedure !== TaxProcedure.REVERSE_CHARGE) {
            return true;
          }
          return !!normalizeVatId(value);
        },
      )
      .optional(),
  }).when('email', {
    is: (email: string | null) => !!email,
    then: (schema) =>
      schema.shape({
        firstName: string().required(),
        lastName: string().required(),
        gender: string().oneOf(Object.values(Gender)).optional(),
        preferredLocal: string().oneOf(Object.values(Local)).required(),
      }),
    otherwise: (schema) => schema,
  }),
});

watch(
  () => [formValues.vatId, formValues.country],
  () => {
    emit('update:tax-inputs', {
      vatId: formValues.vatId,
      country: formValues.country,
    });
  },
  { immediate: true },
);

const submitForm = handleSubmit(async (values) => {
  if (!(await validateVatId(values.vatId, props.customerData.vatId, values.country))) return;
  emit('update:customerData', values);
  emit('continue');
});

const back = () => {
  emit('update:customerData', cloneDeep(formValues));
  emit('back');
};

// Autocomplete customer suggestions
type Suggestion = BookingsControllerSuggestCustomerDataResponse[number];

const searchResultOptions = ref<{ item: Suggestion; content: (string | null | undefined)[] }[]>([]);

const suggestQuery = ref('');
const { data: suggested, isFetching } = useSuggestCustomerData(suggestQuery);

watch([suggested], () => {
  const results = suggested.value ?? [];

  searchResultOptions.value = results.map((s) => {
    const name = [s.customerData.firstName, s.customerData.lastName].filter(Boolean).join(' ');
    const street = s.customerData.street || undefined;
    const zipCityCountry = [
      [s.customerData.zip, s.customerData.city].filter(Boolean).join(' '),
      s.customerData.country,
    ]
      .filter(Boolean)
      .join(', ');
    const email = s.email || undefined;
    const count = (s as any).bookingsCount as number | undefined;
    const bookingNrLine =
      s.source === 'booking' && s.bookingNumber
        ? t('bookingNumber', { bookingNumber: s.bookingNumber })
        : undefined;
    const bookingsCountLine =
      s.source === 'booking' && typeof count === 'number' && count > 1
        ? t('bookingsCount', { count })
        : undefined;
    const summaryLine = bookingsCountLine ?? bookingNrLine;
    return {
      item: s,
      content: [name, street, zipCityCountry, email, summaryLine],
    };
  });
});

const fetchSuggestions = async (q: string) => {
  suggestQuery.value = q ?? '';
};

const handleSelectSuggestion = async (suggested: Suggestion | undefined) => {
  if (!suggested) return;
  setValues({
    ...formValues,
    ...suggested.customerData,
    email: suggested.email,
  });
  if (
    suggested.partner?.id !== props.agency?.id &&
    (suggested.partner || props.agency?.role === UserRole.COMPANY)
  ) {
    const i18nGroup = suggested.partner ? 'usePartner' : 'removePartner';
    const { isConfirmed } = await Alert.fire({
      titleText: t(`${i18nGroup}.title`),
      text: suggested.partner
        ? t(`${i18nGroup}.text`, {
            company: suggested.partner.company,
            name: `${suggested.partner.firstName} ${suggested.partner.lastName}`,
          })
        : undefined,
      showCancelButton: true,
      confirmButtonText: t(`${i18nGroup}.confirm`),
      cancelButtonText: t(`${i18nGroup}.cancel`),
    });
    if (isConfirmed) {
      emit('update-agency', suggested.partner as User | null | undefined);
    }
  }
  emit('suggestion-selected', {
    customerId: suggested.customerId ?? null,
    email: suggested.email ?? null,
  });
  await nextTick();
  emit('apply-tax-procedure-suggestion');
};
</script>

<i18n lang="json">
{
  "en": {
    "customerData": "Customer Data",
    "createBooking": "Create Booking",
    "email": "Email",
    "bookingSummary": "Booking Summary",
    "searchCustomer": "Search customer",
    "searchByNameEmailOrNumber": "Search by name, email or booking number",
    "bookingNumber": "Booking {bookingNumber}",
    "bookingsCount": "({count} bookings)",
    "usePartner": {
      "title": "Update linked agency?",
      "text": "This user is linked with following partner: {company} {name}",
      "confirm": "Link agency",
      "cancel": "Do not update"
    },
    "removePartner": {
      "title": "Remove linked agency?",
      "confirm": "Unlink agency",
      "cancel": "Do not update"
    }
  },
  "de": {
    "customerData": "Kundendaten",
    "createBooking": "Buchung erstellen",
    "email": "Email",
    "bookingSummary": "Buchungsübersicht",
    "searchCustomer": "Kunden suchen",
    "searchByNameEmailOrNumber": "Suche nach Name, Email oder Buchungsnummer",
    "bookingNumber": "Buchung {bookingNumber}",
    "bookingsCount": "({count} Buchungen)",
    "usePartner": {
      "title": "Zugeordneten Vermittler inkl. Konditionen aktualisieren?",
      "text": "Dieser Benutzer ist folgendem Partner zugeordnet: {company} {name}",
      "confirm": "Vermittler übernehmen",
      "cancel": "Nicht aktualisieren"
    },
    "removePartner": {
      "title": "Zugeordneten Vermittler inkl. Konditionen entfernen?",
      "confirm": "Vermittler entfernen",
      "cancel": "Nicht aktualisieren"
    }
  },
  "es": {
    "customerData": "Datos del cliente",
    "createBooking": "Crear reserva",
    "email": "Correo electrónico",
    "bookingSummary": "Resumen de la reserva",
    "searchCustomer": "Buscar cliente",
    "searchByNameEmailOrNumber": "Buscar por nombre, correo o n.º de reserva",
    "bookingNumber": "Reserva {bookingNumber}",
    "bookingsCount": "({count} reservas)",
    "usePartner": {
      "title": "¿Actualizar la agencia vinculada?",
      "text": "Este usuario está vinculado al socio: {company} {name}",
      "confirm": "Vincular agencia",
      "cancel": "No actualizar"
    },
    "removePartner": {
      "title": "¿Quitar la agencia vinculada?",
      "confirm": "Desvincular agencia",
      "cancel": "No actualizar"
    }
  }
}
</i18n>
